import React from 'react';
import { ClientContext } from '~/services';
import moment from 'moment';
import { ReferralType, enumValue, formatAddress, formatDiagnosisCodesForPrintout } from '~/util';
import { ProviderEntityEnum, ProviderType, getProviderInfo } from '~/util/providerUtils';
import {
    CareRequestTypeOptionType,
    IcdCodeOptionType,
    ReferralOrgFieldsState,
} from '~/components/Patient/Referrals/ReferralInfo/ReferralInfo';

interface ProviderPrintProps {
    referralStatus: 'confirmed' | 'unconfirmed';
    patient: PatientType;
    provider: ProviderType;
    reason: string;
    notes: string;
    isSubmitDisabled?: boolean;
    sentReferral?: ReferralType;
    patientEmail?: string;
    location?: undefined | LocationType;
    btnClass?: string;
    btnText?: string;
    orgFormFields?: ReferralOrgFieldsState;
    startDate?: Date | null;
    endDate?: Date | null;
    diagnosisCodes?: IcdCodeOptionType[];
    visitNumber?: number | null;
    careRequestType?: CareRequestTypeOptionType | null;
    fromProviderName?: string;
}

export function ProviderPrint({
    referralStatus,
    reason,
    notes,
    provider,
    patient,
    isSubmitDisabled = false,
    sentReferral,
    patientEmail = '',
    location,
    btnClass = 'btn-primary',
    btnText = 'Patient Printout',
    orgFormFields,
    startDate,
    endDate,
    diagnosisCodes = [],
    visitNumber,
    careRequestType,
    fromProviderName,
}: ProviderPrintProps) {
    const { reportYear } = React.useContext(ClientContext);
    const { providerName, providerIsContact } = getProviderInfo(provider);
    function printProvider() {
        const params = new URLSearchParams();
        params.append('referralStatus', referralStatus);
        params.append('reason', reason);
        params.append('notes', notes);
        params.append('printed', moment().format('M/DD/Y h:mma'));
        params.append(
            'sentDate',
            sentReferral ? moment(sentReferral.sent_on).format('M/DD/Y h:mma') : ''
        );
        // Only include these params if the corresponding fields exist in orgFormFields
        if (orgFormFields?.startDate) {
            params.append(
                'send_referral_start_date',
                startDate ? moment(startDate).format('M/DD/Y') : ''
            );
        }
        if (orgFormFields?.endDate) {
            params.append(
                'send_referral_end_date',
                endDate ? moment(endDate).format('M/DD/Y') : ''
            );
        }
        if (orgFormFields?.icdCode) {
            params.append(
                'send_referral_diagnosis_code',
                formatDiagnosisCodesForPrintout(diagnosisCodes)
            );
        }
        if (orgFormFields?.visitNumber) {
            params.append(
                'send_referral_visit_number',
                visitNumber !== null ? String(visitNumber) : ''
            );
        }
        if (orgFormFields?.careRequestType) {
            params.append('send_referral_care_request_type', careRequestType?.label ?? '');
        }
        params.append('providerName', providerName);
        params.append('providerClinic', `${provider?.orgName || provider?.practiceName || ''}`);
        params.append('referralId', sentReferral?.referral_message.base36Id || '');
        params.append('patientEmail', patientEmail);
        if (fromProviderName) {
            params.append('fromProvider', fromProviderName);
        }
        let providerPhone: undefined | PhoneType;
        if (providerIsContact) {
            providerPhone = provider.phones?.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice'])
            );
        } else {
            const locationPhone = location?.phones.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice'])
            );
            const organizationPhones =
                provider.entityType === ProviderEntityEnum.ORG
                    ? provider.phones
                    : provider.organization?.phones;
            const organizationPhone = organizationPhones?.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Main'])
            );
            providerPhone = locationPhone || organizationPhone;
        }
        params.append(
            'providerPhone',
            providerPhone
                ? `${providerPhone.number}${
                      providerPhone.extension ? ` Ext. ${providerPhone.extension}` : ''
                  }`
                : ''
        );

        params.append(
            'providerLocation',
            location && location.addresses.length > 0
                ? formatAddress(location.addresses[0]).join('\n')
                : ''
        );
        const pdfWindow = window.open(
            `/api/patients/${patient.id}/${reportYear}/referralPdf?${params}`
        );
        pdfWindow?.print();
    }
    return (
        <button
            type="button"
            className={`btn ${btnClass}`}
            disabled={isSubmitDisabled}
            onClick={printProvider}
        >
            {btnText}
        </button>
    );
}
